import { Calendar, ChevronDown } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { dayjs } from "@/utils/date";

interface PeriodPreset {
  id: string;
  label: string;
  range: () => { start: string; end: string };
}

interface PeriodSelectorProps {
  periodPresets?: PeriodPreset[];
  value: { start: string; end: string };
  onChange: (range: { start: string; end: string }) => void;
  className?: string;
  noCustom?: boolean;
}

const DEFAULT_PERIOD_PRESETS: PeriodPreset[] = [
  {
    id: "24h",
    label: "Últimas 24h",
    range: () => {
      const end = dayjs();
      const start = end.clone().subtract(1, "day");
      return { start: start.format("YYYY-MM-DD"), end: end.format("YYYY-MM-DD") };
    },
  },
  {
    id: "7d",
    label: "Últimos 7 dias",
    range: () => {
      const end = dayjs();
      const start = end.clone().subtract(7, "day");
      return { start: start.format("YYYY-MM-DD"), end: end.format("YYYY-MM-DD") };
    },
  },
  {
    id: "30d",
    label: "Últimos 30 dias",
    range: () => {
      const end = dayjs();
      const start = end.clone().subtract(30, "day");
      return { start: start.format("YYYY-MM-DD"), end: end.format("YYYY-MM-DD") };
    },
  },
  {
    id: "currentMonth",
    label: "Mês atual",
    range: () => {
      const end = dayjs();
      const start = end.clone().startOf("month");
      return { start: start.format("YYYY-MM-DD"), end: end.format("YYYY-MM-DD") };
    },
  },
  {
    id: "previousMonth",
    label: "Mês anterior",
    range: () => {
      const end = dayjs().startOf("month").subtract(1, "day").endOf("day");
      const start = end.clone().startOf("month");
      return { start: start.format("YYYY-MM-DD"), end: end.format("YYYY-MM-DD") };
    },
  },
  {
    id: "custom",
    label: "Período personalizado",
    range: () => {
      // Return current date range for custom (this will be handled differently)
      const end = dayjs();
      const start = end.clone().subtract(1, "day");
      return { start: start.format("YYYY-MM-DD"), end: end.format("YYYY-MM-DD") };
    },
  },
];

function PeriodSelector({
  periodPresets = DEFAULT_PERIOD_PRESETS,
  value,
  onChange,
  className = "",
  noCustom = false,
}: PeriodSelectorProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customRange, setCustomRange] = useState(value);
  const customRangeRef = useRef<HTMLDivElement>(null);

  // Determine the current period display text
  const getCurrentPeriodText = () => {
    const preset = periodPresets.find(
      (p) => p.id !== "custom" && p.range().start === value.start && p.range().end === value.end
    );
    if (preset) {
      return preset.label;
    }
    return "Período personalizado";
  };

  // Format date range for display
  const getCustomDateRangeText = () => {
    if (value.start && value.end) {
      const start = dayjs(value.start);
      const end = dayjs(value.end);
      return `${start.format("DD/MM/YYYY")} - ${end.format("DD/MM/YYYY")}`;
    }
    return "";
  };

  const availablePresets = noCustom ? periodPresets.filter((p) => p.id !== "custom") : periodPresets;

  const handlePresetSelect = useCallback(
    (presetId: string) => {
      if (presetId === "custom") {
        setShowCustomRange(true);
        setCustomRange(value);
        // Auto-scroll to custom range section
        setTimeout(() => {
          customRangeRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }, 100);
        return;
      }

      const preset = periodPresets.find((p) => p.id === presetId);
      if (preset) {
        const range = preset.range();
        onChange(range);
        setIsDropdownOpen(false);
      }
    },
    [periodPresets, onChange, value]
  );

  const handleApplyCustomRange = useCallback(() => {
    if (customRange.start && customRange.end) {
      onChange(customRange);
      setIsDropdownOpen(false);
      setShowCustomRange(false);
    }
  }, [customRange, onChange]);

  const handleCancelCustomRange = useCallback(() => {
    setShowCustomRange(false);
    setCustomRange(value);
    setIsDropdownOpen(false);
  }, [value]);

  return (
    <div className={className}>
      <div className="relative">
        <button
          type="button"
          className="w-full flex items-center justify-start px-4 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        >
          <Calendar className="text-gray-400" size={20} />
          <div className="flex items-center justify-between gap-2 ml-2 flex-1">
            <div className="text-left">
              <span className="font-medium text-gray-900">{getCurrentPeriodText()}</span>
              {periodPresets.find(
                (p) => p.id !== "custom" && p.range().start === value.start && p.range().end === value.end
              ) ? null : (
                <span className="block text-xs text-gray-500">{getCustomDateRangeText()}</span>
              )}
            </div>
            <ChevronDown
              className={`text-gray-400 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
              size={20}
            />
          </div>
        </button>

        {isDropdownOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
            <div className="py-2">
              {availablePresets.map((preset) => (
                <button
                  type="button"
                  key={preset.id}
                  className="w-full flex items-center px-4 py-3 text-left hover:bg-gray-50 transition-colors first:rounded-t-lg"
                  onClick={() => handlePresetSelect(preset.id)}
                >
                  <span
                    className={`w-2 h-2 rounded-full mr-3 ${
                      preset.id === "custom"
                        ? "bg-blue-500"
                        : periodPresets.find(
                              (p) =>
                                p.id === preset.id && p.range().start === value.start && p.range().end === value.end
                            )
                          ? "bg-green-500"
                          : "bg-gray-300"
                    }`}
                  />
                  <span className="text-gray-900">{preset.label}</span>
                </button>
              ))}
            </div>

            {showCustomRange && (
              <div ref={customRangeRef} className="border-t border-gray-200 p-4">
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">Data de início</label>
                    <input
                      type="date"
                      value={customRange.start}
                      onChange={(e) => setCustomRange((prev) => ({ ...prev, start: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">Data de fim</label>
                    <input
                      type="date"
                      value={customRange.end}
                      onChange={(e) => setCustomRange((prev) => ({ ...prev, end: e.target.value }))}
                      min={customRange.start}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleApplyCustomRange}
                      className="flex-1 bg-green-600 text-white py-2 px-3 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                      disabled={!customRange.start || !customRange.end}
                    >
                      Aplicar
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelCustomRange}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 px-3 rounded-lg hover:bg-gray-400 transition-colors text-sm font-medium"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default PeriodSelector;
